'use client';

import * as React from 'react';
import { Building2, ChevronDown, Plus, Check, Loader2, UserCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSidebar } from '@/components/ui/sidebar';
import { type Business } from '@/hooks/react-query/business/use-business';
import { useBusiness } from '@/contexts/business-context';
import { useIsAccountant } from '@/hooks/use-accountant';
import { toast } from 'sonner';

interface BusinessSelectorProps {
  className?: string;
}

export function BusinessSelector({ className }: BusinessSelectorProps) {
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';
  const { data: isAccountant } = useIsAccountant();
  
  const {
    businesses,
    activeBusiness,
    isLoadingBusinesses,
    isLoadingActive,
    isSwitching,
    isCreating,
    switchBusiness,
    createBusiness: createNewBusiness,
  } = useBusiness();
  
  const [showNewBusinessDialog, setShowNewBusinessDialog] = React.useState(false);
  const [newBusinessName, setNewBusinessName] = React.useState('');
  
  const handleSelectBusiness = async (business: Business) => {
    if (business.id === activeBusiness?.id) return;
    
    try {
      await switchBusiness(business.id);
      toast.success(`Switched to ${business.name}`);
    } catch (error) {
      toast.error('Failed to switch business');
    }
  };
  
  const handleCreateBusiness = async () => {
    if (!newBusinessName.trim()) {
      toast.error('Please enter a business name');
      return;
    }
    
    try {
      await createNewBusiness(newBusinessName.trim());
      toast.success(`Created business "${newBusinessName.trim()}"`);
      setShowNewBusinessDialog(false);
      setNewBusinessName('');
    } catch (error) {
      toast.error('Failed to create business');
    }
  };
  
  const isLoading = isLoadingBusinesses || isLoadingActive;
  
  if (isLoading) {
    return (
      <div className={cn('px-2', className)}>
        <div className="flex items-center gap-2 h-9 px-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          {!isCollapsed && (
            <span className="text-sm text-muted-foreground">Loading...</span>
          )}
        </div>
      </div>
    );
  }
  
  if (!businesses || businesses.length === 0) {
    // For accountants with no assigned businesses, show a different message
    if (isAccountant) {
      return (
        <div className={cn('px-2', className)}>
          <div className="flex items-center gap-2 h-9 px-2 text-muted-foreground">
            <UserCheck className="h-4 w-4" />
            {!isCollapsed && (
              <span className="text-sm">No businesses assigned</span>
            )}
          </div>
        </div>
      );
    }
    
    return (
      <div className={cn('px-2', className)}>
        <Button
          variant="ghost"
          className="w-full justify-start h-9 px-2"
          onClick={() => setShowNewBusinessDialog(true)}
        >
          <Building2 className="h-4 w-4 mr-2" style={{ color: '#ff6700' }} />
          {!isCollapsed && (
            <span className="text-sm">Create Business</span>
          )}
        </Button>
        
        <Dialog open={showNewBusinessDialog} onOpenChange={setShowNewBusinessDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <Building2 className="h-5 w-5 mr-2" style={{ color: '#ff6700' }} />
                Create Your First Business
              </DialogTitle>
              <DialogDescription>
                Enter a name for your business. You can add more businesses later.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Business Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Acme Corporation"
                  value={newBusinessName}
                  onChange={(e) => setNewBusinessName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateBusiness()}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewBusinessDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateBusiness}
                disabled={isCreating}
                style={{ backgroundColor: '#ff6700' }}
                className="hover:bg-orange-600"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Business'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  
  return (
    <div className={cn('px-2', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              'w-full h-9 px-2 hover:bg-orange-50/50',
              isCollapsed ? 'justify-center' : 'justify-between'
            )}
          >
            <div className="flex items-center min-w-0">
              <Building2 
                className="h-4 w-4 shrink-0" 
                style={{ color: '#ff6700' }} 
              />
              {!isCollapsed && (
                <span className="ml-2 truncate text-sm font-medium">
                  {activeBusiness?.name || 'Select Business'}
                </span>
              )}
            </div>
            {!isCollapsed && (
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
          align="start"
        >
          <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-2">
            {isAccountant ? (
              <>
                <UserCheck className="h-3 w-3" />
                Assigned Businesses
              </>
            ) : (
              'Your Businesses'
            )}
          </DropdownMenuLabel>
          {businesses.map((business) => (
            <DropdownMenuItem
              key={business.id}
              onClick={() => handleSelectBusiness(business)}
              className="cursor-pointer"
            >
              <Building2 
                className="h-4 w-4 mr-2" 
                style={{ color: business.id === activeBusiness?.id ? '#ff6700' : undefined }}
              />
              <span className="flex-1 truncate">{business.name}</span>
              {business.id === activeBusiness?.id && (
                <Check className="h-4 w-4 ml-2" style={{ color: '#ff6700' }} />
              )}
            </DropdownMenuItem>
          ))}
          {/* Only show "Add New Business" for non-accountant users */}
          {!isAccountant && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowNewBusinessDialog(true)}
                className="cursor-pointer"
              >
                <Plus className="h-4 w-4 mr-2" style={{ color: '#ff6700' }} />
                <span>Add New Business</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      
      {/* New Business Dialog */}
      <Dialog open={showNewBusinessDialog} onOpenChange={setShowNewBusinessDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Building2 className="h-5 w-5 mr-2" style={{ color: '#ff6700' }} />
              Create New Business
            </DialogTitle>
            <DialogDescription>
              Add a new business to your account. You can switch between businesses anytime.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="new-business-name">Business Name</Label>
              <Input
                id="new-business-name"
                placeholder="e.g., Acme Corporation"
                value={newBusinessName}
                onChange={(e) => setNewBusinessName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateBusiness()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewBusinessDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateBusiness}
              disabled={isCreating}
              style={{ backgroundColor: '#ff6700' }}
              className="hover:bg-orange-600"
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Business'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
